import { Box } from '@zoopla-eng/zds/box';
import { ChevronDownMedium } from '@zoopla-eng/zds/icons';
import { Stack } from '@zoopla-eng/zds/stack';
import { fontGroups } from '@zoopla-eng/zds/themes';
import {
    iconContainerStyle,
    selectWrapperStyle,
    styledLabelStyle,
    styledSelectStyle
} from './Filters.styles.css';
import { priceWrapperStyle } from './Price.styles.css';

const forSalePrices = [
    '10000',
    '20000',
    '30000',
    '40000',
    '50000',
    '60000',
    '70000',
    '80000',
    '90000',
    '100000',
    '110000',
    '120000',
    '125000',
    '130000',
    '140000',
    '150000',
    '160000',
    '170000',
    '180000',
    '190000',
    '200000',
    '210000',
    '220000',
    '230000',
    '240000',
    '250000',
    '275000',
    '300000',
    '325000',
    '350000',
    '375000',
    '400000',
    '425000',
    '450000',
    '475000',
    '500000',
    '550000',
    '600000',
    '650000',
    '700000',
    '750000',
    '800000',
    '850000',
    '900000',
    '950000',
    '1000000',
    '1100000',
    '1200000',
    '1300000',
    '1400000',
    '1500000',
    '1600000',
    '1700000',
    '1800000',
    '1900000',
    '2000000',
    '2100000',
    '2200000',
    '2300000',
    '2400000',
    '2500000',
    '2750000',
    '3000000',
    '3250000',
    '3500000',
    '3750000',
    '4000000',
    '4250000',
    '4500000',
    '4750000',
    '5000000',
    '5500000',
    '6000000',
    '6500000',
    '7000000',
    '7500000',
    '8000000',
    '8500000',
    '9000000',
    '9500000',
    '10000000',
    '12500000',
    '15000000'
];

const toRentPrices = [
    '25',
    '50',
    '75',
    '100',
    '125',
    '150',
    '175',
    '200',
    '225',
    '250',
    '275',
    '300',
    '325',
    '350',
    '375',
    '400',
    '425',
    '450',
    '475',
    '500',
    '550',
    '600',
    '650',
    '700',
    '750',
    '800',
    '850',
    '900',
    '950',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2500',
    '3000',
    '3500',
    '4000',
    '4500',
    '5000',
    '7500',
    '8000',
    '8500',
    '9000',
    '9500',
    '10000',
    '12500',
    '15000',
    '17500',
    '20000',
    '25000'
];

type prices = typeof forSalePrices[number] & typeof toRentPrices[number];

export type PriceFrequency = '' | 'per_week' | 'per_month' | string;
export type PriceMin = prices;
export type PriceMax = prices;

type PriceProps = {
    value: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    };
    onChange: ({
        priceMin,
        priceMax,
        priceFrequency
    }: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    }) => void;
};

export const Price = ({ value, onChange }: PriceProps) => {
    const priceOptions = value.priceFrequency ? toRentPrices : forSalePrices;

    const priceMinOptions =
        value.priceMax !== ''
            ? priceOptions.slice(0, priceOptions.indexOf(value.priceMax) + 1)
            : priceOptions;

    const priceMaxOptions =
        value.priceMin !== ''
            ? priceOptions.slice(priceOptions.indexOf(value.priceMin))
            : priceOptions;

    return (
        <>
            {value.priceFrequency ? (
                <Box pb="S300">
                    <div className={selectWrapperStyle}>
                        <label
                            className={`${styledLabelStyle} ${fontGroups.ladle}`}
                            htmlFor="price_frequency"
                        >
                            Price Per
                        </label>
                        <select
                            className={`${styledSelectStyle} ${fontGroups.ladle}`}
                            name="price_frequency"
                            id="price_frequency"
                            onChange={(e) => {
                                onChange({
                                    ...value,
                                    priceFrequency: e.target.value
                                });
                            }}
                            value={value.priceFrequency}
                        >
                            <option value="per_month">Month</option>
                            <option value="per_week">Week</option>
                        </select>
                        <div className={iconContainerStyle}>
                            <ChevronDownMedium />
                        </div>
                    </div>
                </Box>
            ) : null}
            <Stack
                flexDirection="row"
                flexWrap="wrap"
                columnGap="S400"
                rowGap="S400"
            >
                <div className={priceWrapperStyle}>
                    <div className={selectWrapperStyle}>
                        <label
                            className={`${styledLabelStyle} ${fontGroups.ladle}`}
                            htmlFor="price_min"
                        >
                            Min price
                        </label>
                        <select
                            className={`${styledSelectStyle} ${fontGroups.ladle}`}
                            name="price_min"
                            id="price_min"
                            onChange={(e) => {
                                onChange({
                                    ...value,
                                    priceMin: e.target.value
                                });
                            }}
                            value={value.priceMin}
                        >
                            <option value="">No min</option>
                            {priceMinOptions.map((priceMinOption, index) => {
                                const price = new Intl.NumberFormat('en-GB', {
                                    style: 'currency',
                                    currency: 'GBP',
                                    maximumFractionDigits: 0,
                                    minimumFractionDigits: 0
                                }).format(Number(priceMinOption));

                                return (
                                    <option
                                        value={priceMinOption}
                                        key={`price_min_${index}`}
                                    >
                                        {price}
                                    </option>
                                );
                            })}
                        </select>
                        <div className={iconContainerStyle}>
                            <ChevronDownMedium />
                        </div>
                    </div>
                </div>
                <div className={priceWrapperStyle}>
                    <div className={selectWrapperStyle}>
                        <label
                            className={`${styledLabelStyle} ${fontGroups.ladle}`}
                            htmlFor="price_max"
                        >
                            Max price
                        </label>
                        <select
                            className={`${styledSelectStyle} ${fontGroups.ladle}`}
                            id="price_max"
                            name="price_max"
                            onChange={(e) => {
                                onChange({
                                    ...value,
                                    priceMax: e.target.value
                                });
                            }}
                            value={value.priceMax}
                        >
                            <option value="">No max</option>
                            {priceMaxOptions.map((priceMaxOption, index) => {
                                const price = new Intl.NumberFormat('en-GB', {
                                    style: 'currency',
                                    currency: 'GBP',
                                    maximumFractionDigits: 0,
                                    minimumFractionDigits: 0
                                }).format(Number(priceMaxOption));

                                return (
                                    <option
                                        value={priceMaxOption}
                                        key={`price_max_${index}`}
                                    >
                                        {price}
                                    </option>
                                );
                            })}
                        </select>
                        <div className={iconContainerStyle}>
                            <ChevronDownMedium />
                        </div>
                    </div>
                </div>
            </Stack>
        </>
    );
};
